import { useProperty, useTranslation } from '@eti/providers';
import { breakpoints, Button, Stack } from '@etg/wings';
import { css } from '@eti/styles';
import { useFormContext } from 'react-hook-form';
import type { FieldErrors } from 'react-hook-form';
import type { SearchFormValues } from 'client/src/common/search-form-panel/SearchFormNewContainer';
import { forwardRef } from 'react';
import DirectFlightCheckBoxNew from './DirectFlightCheckBoxNew';
import ZeroWidthSpace from './ZeroWidthSpace';

const getButtonWrapperStyles = (maxHeight: string) => css`
  max-height: ${maxHeight};
  place-content: space-between;
  transition: max-height 0.2s;

  @media (min-width: ${breakpoints._768}) {
    flex-direction: column-reverse;
  }
`;

const buttonStyles = css`
  width: 100%;

  @media (min-width: ${breakpoints._970}) {
    min-width: 11.25rem;
  }
`;

const getWrapperMaxHeight = (errors: FieldErrors<SearchFormValues>) => {
  const { origin, destination, departureDate, returnDate } = errors?.singleBound || {};
  const hasLocationError = origin || destination;
  const hasDateError = departureDate || returnDate;

  if (hasLocationError) {
    return '190px';
  }
  if (hasDateError) {
    return '165px';
  }
  return 'fit-content';
};

type SubmitButtonNewProps = {
  areOriginAndDestinationFilled?: boolean;
  isMultiStop?: boolean;
  isStopoverEnabled?: boolean;
};

const SubmitButtonNew = forwardRef<HTMLAnchorElement, SubmitButtonNewProps>(
  ({ areOriginAndDestinationFilled, isMultiStop, isStopoverEnabled }, ref) => {
    const { t } = useTranslation();
    const { p } = useProperty();
    const {
      formState: { errors },
    } = useFormContext<SearchFormValues>();
    const maxWrapperHeight = getWrapperMaxHeight(errors);

    const showDirectFlight =
      !p('SearchForm.ShowExtendedForm.Enabled') &&
      !isMultiStop &&
      areOriginAndDestinationFilled &&
      !p('Searchform.Stopover.Enabled');

    const redirectHref: string = p('SearchForm.RedirectHref.Value');
    const isLink = Boolean(p('SearchForm.RedirectHref.Enabled')) && Boolean(redirectHref);

    return (
      <Stack
        className={!isStopoverEnabled ? getButtonWrapperStyles(maxWrapperHeight) : undefined}
        spacing={24}
      >
        {showDirectFlight && !isStopoverEnabled && (
          <Stack spacing={4}>
            <ZeroWidthSpace isDesktopOnly />
            <DirectFlightCheckBoxNew />
          </Stack>
        )}
        <Stack spacing={4}>
          <ZeroWidthSpace isDesktopOnly />
          <Button
            as={isLink ? 'a' : undefined}
            className={buttonStyles}
            data-testid={
              isLink ? 'searchForm-searchFlights-link' : 'searchForm-searchFlights-button'
            }
            href={isLink ? redirectHref : undefined}
            ref={ref}
            type={isLink ? undefined : 'submit'}
            variant="primary"
          >
            {t('Button.Text.Search.Air')}
          </Button>
        </Stack>
      </Stack>
    );
  },
);

export default SubmitButtonNew;
